import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useProjectStore } from '../../store/projectStore';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiBriefcase, FiUsers, FiTrendingUp, FiClock } = FiIcons;

const DashboardHome = () => {
  const { projects, prospects } = useProjectStore();

  const stats = [
    {
      title: 'Proyectos Activos',
      value: projects.filter(p => p.status === 'active').length,
      icon: FiBriefcase,
      color: 'bg-blue-500',
      trend: '+12%'
    },
    {
      title: 'Total Prospectos',
      value: prospects.length,
      icon: FiUsers,
      color: 'bg-green-500',
      trend: '+8%'
    },
    {
      title: 'Entrevistas Completadas',
      value: prospects.filter(p => p.status !== 'new').length,
      icon: FiTrendingUp,
      color: 'bg-purple-500',
      trend: '+15%'
    },
    {
      title: 'Pendientes de Revisión',
      value: prospects.filter(p => p.status === 'new').length,
      icon: FiClock,
      color: 'bg-orange-500',
      trend: '+3%'
    }
  ];

  const recentProjects = projects.slice(0, 3);

  return (
    <div className="p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          ¡Bienvenido de vuelta!
        </h1>
        <p className="text-gray-600">
          Aquí tienes un resumen de tu actividad reciente en VIXEA
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                <SafeIcon icon={stat.icon} className="w-6 h-6 text-white" />
              </div>
              <span className="text-green-600 text-sm font-medium">{stat.trend}</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
            <p className="text-gray-600 text-sm">{stat.title}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Recent Projects */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Proyectos Recientes</h2>
            <Link
              to="/dashboard/projects"
              className="text-primary hover:text-blue-700 text-sm font-medium"
            >
              Ver todos
            </Link>
          </div>

          {recentProjects.length > 0 ? (
            <div className="space-y-4">
              {recentProjects.map((project) => (
                <div key={project.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h3 className="font-semibold text-gray-900">{project.title}</h3>
                    <p className="text-sm text-gray-600">{project.company}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium text-gray-900">
                      {project.responses} respuestas
                    </span>
                    <p className="text-xs text-gray-500">
                      ID: {project.vixeaId}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <SafeIcon icon={FiBriefcase} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No hay proyectos aún</p>
              <Link
                to="/dashboard/projects/new"
                className="inline-flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Crear Proyecto</span>
              </Link>
            </div>
          )}
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-6">Acciones Rápidas</h2>
          <div className="space-y-4">
            <Link
              to="/dashboard/projects/new"
              className="flex items-center space-x-4 p-4 bg-primary text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-6 h-6" />
              <div>
                <h3 className="font-semibold">Crear Nuevo Proyecto</h3>
                <p className="text-sm text-blue-100">Configura una nueva entrevista</p>
              </div>
            </Link>

            <Link
              to="/dashboard/prospects"
              className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <SafeIcon icon={FiUsers} className="w-6 h-6 text-gray-600" />
              <div>
                <h3 className="font-semibold text-gray-900">Ver Prospectos</h3>
                <p className="text-sm text-gray-600">Revisar candidatos pendientes</p>
              </div>
            </Link>

            <Link
              to="/dashboard/analytics"
              className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-gray-600" />
              <div>
                <h3 className="font-semibold text-gray-900">Ver Análisis</h3>
                <p className="text-sm text-gray-600">Estadísticas y métricas</p>
              </div>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardHome;